import React, { useState, useEffect, useRef } from 'react';
import '../styles/global.css';
import '../styles/tabs.css';

/**
 * Navigation component for switching between content sections
 * Provides responsive tab sizing with horizontal scrolling on mobile
 * @param {Object} props - Component props
 * @param {string} props.activeSection - Currently active section
 * @param {Function} props.onSectionChange - Function to call when section changes
 * @returns {JSX.Element} Navigation component
 */
const Navigation = ({ activeSection, onSectionChange }) => {
  // Define the available sections with full labels
  const sections = [
    { id: 'key', fullLabel: 'Key Takeaways' },
    { id: 'summary', fullLabel: 'Summary Video' },
    { id: 'notes', fullLabel: 'Detailed Notes' },
    { id: 'full', fullLabel: 'Full Video' },
    { id: 'activity', fullLabel: 'Activity' }
  ];

  // Reference to the tab container for scrolling
  const tabContainerRef = useRef(null);
  
  // State to track screen size for component logic separation
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  
  // Update screen size state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    
    // Set initial state
    handleResize();
    
    // Add event listener for window resize
    window.addEventListener('resize', handleResize);
    
    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Function to scroll active tab into view (only on mobile)
  const scrollActiveTabIntoView = () => {
    if (!isMobile || !tabContainerRef.current) return;
    
    const activeTabElement = tabContainerRef.current.querySelector('.tab-item.active');
    if (!activeTabElement) return;
    
    // Calculate position to center the active tab
    const containerWidth = tabContainerRef.current.offsetWidth;
    const tabWidth = activeTabElement.offsetWidth;
    const tabLeft = activeTabElement.offsetLeft;
    
    // Center the active tab
    tabContainerRef.current.scrollLeft = tabLeft - (containerWidth / 2) + (tabWidth / 2);
  };
  
  // Scroll active tab into view on initial render and when active section changes
  useEffect(() => {
    scrollActiveTabIntoView();
  }, [activeSection, isMobile]);

  // Set up touch event handlers to prevent page scrolling during tab navigation
  useEffect(() => {
    const tabContainer = tabContainerRef.current;
    if (!tabContainer || !isMobile) return;
    
    // Variables to track touch movement
    let startX, startY;
    let initialScrollLeft;
    let isScrolling = false;
    
    // Touch start event
    const handleTouchStart = (e) => {
      // Store the initial touch position and scroll position
      startX = e.touches[0].pageX;
      startY = e.touches[0].pageY;
      initialScrollLeft = tabContainer.scrollLeft;
      isScrolling = true;
    };
    
    // Touch move event
    const handleTouchMove = (e) => {
      if (!isScrolling) return;
      
      // Calculate movement
      const touchX = e.touches[0].pageX;
      const touchY = e.touches[0].pageY;
      const deltaX = startX - touchX;
      const deltaY = startY - touchY;
      
      // If horizontal scrolling is more significant than vertical scrolling
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        // Prevent the default page scroll
        e.preventDefault();
        
        // Move the scroll position of the tab container
        tabContainer.scrollLeft = initialScrollLeft + deltaX;
      }
    };
    
    // Touch end event
    const handleTouchEnd = () => {
      isScrolling = false;
    };
    
    // Add event listeners
    tabContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
    tabContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
    tabContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
    tabContainer.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    
    // Cleanup function to remove event listeners
    return () => {
      tabContainer.removeEventListener('touchstart', handleTouchStart);
      tabContainer.removeEventListener('touchmove', handleTouchMove);
      tabContainer.removeEventListener('touchend', handleTouchEnd);
      tabContainer.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isMobile]); // Re-run when mobile state changes

  return (
    <div className="tab-container" ref={tabContainerRef}>
      {sections.map(section => (
        <button
          key={section.id}
          className={`tab-item ${activeSection === section.id ? 'active' : ''}`}
          onClick={() => onSectionChange(section.id)}
          data-tab={section.id}
          aria-selected={activeSection === section.id}
          role="tab"
        >
          {section.fullLabel}
        </button>
      ))}
    </div>
  );
};

export default Navigation;
